import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Snackbar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  CheckCircle,
  AutoAwesome,
  TrendingUp,
  Psychology,
  Schedule,
  Notifications,
  Send,
  Save,
  Error,
  Info,
  Close,
  ArrowBack,
  ArrowForward,
  School
} from '@mui/icons-material';

const ClientOnboarding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    service: '',
    autoContact: false,
    platforms: ['linkedin', 'facebook'],
    keywords: [],
    geoTargeting: {
      enabled: false,
      locations: [],
      radius: 50
    }
  });
  const [customKeyword, setCustomKeyword] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stepLoading, setStepLoading] = useState(false);

  // Load saved progress on component mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('onboardingProgress');
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        setFormData(parsed.formData);
        setActiveStep(parsed.activeStep);
      } catch (error) {
        console.error('Error loading saved progress:', error);
      }
    }
  }, []);

  // Save progress whenever form data changes
  useEffect(() => {
    if (activeStep > 0) {
      localStorage.setItem('onboardingProgress', JSON.stringify({
        formData,
        activeStep
      }));
    }
  }, [formData, activeStep]);

  const industries = [
    { value: 'real-estate', label: 'Real Estate', keywords: ['buying', 'selling', 'mortgage', 'property'] },
    { value: 'plumbing', label: 'Plumbing', keywords: ['repair', 'emergency', 'leak', 'installation'] },
    { value: 'healthcare', label: 'Healthcare', keywords: ['appointment', 'treatment', 'consultation', 'care'] },
    { value: 'fitness', label: 'Fitness', keywords: ['training', 'workout', 'personal trainer', 'gym'] },
    { value: 'attorney', label: 'Attorney', keywords: ['legal', 'consultation', 'case', 'lawyer'] },
    { value: 'marketing', label: 'Marketing', keywords: ['campaign', 'advertising', 'branding', 'social media'] },
    { value: 'consulting', label: 'Consulting', keywords: ['strategy', 'business', 'growth', 'advisory'] },
    { value: 'technology', label: 'Technology', keywords: ['software', 'development', 'IT', 'digital'] }
  ];

  const platforms = [
    { value: 'linkedin', label: 'LinkedIn', description: 'Professional networking and B2B leads' },
    { value: 'facebook', label: 'Facebook', description: 'Local business and community engagement' },
    { value: 'instagram', label: 'Instagram', description: 'Visual content and brand awareness' },
    { value: 'twitter', label: 'Twitter', description: 'Real-time conversations and industry discussions' }
  ];

  const popularLocations = [
    // South Carolina
    { value: 'myrtle beach, sc', label: 'Myrtle Beach, SC', category: 'South Carolina' },
    { value: 'north myrtle beach, sc', label: 'North Myrtle Beach, SC', category: 'South Carolina' },
    { value: 'surfside beach, sc', label: 'Surfside Beach, SC', category: 'South Carolina' },
    { value: 'charleston, sc', label: 'Charleston, SC', category: 'South Carolina' },
    { value: 'columbia, sc', label: 'Columbia, SC', category: 'South Carolina' },
    { value: 'greenville, sc', label: 'Greenville, SC', category: 'South Carolina' },
    // Major US Cities
    { value: 'new york, ny', label: 'New York, NY', category: 'Major US Cities' },
    { value: 'los angeles, ca', label: 'Los Angeles, CA', category: 'Major US Cities' },
    { value: 'chicago, il', label: 'Chicago, IL', category: 'Major US Cities' },
    { value: 'houston, tx', label: 'Houston, TX', category: 'Major US Cities' },
    { value: 'phoenix, az', label: 'Phoenix, AZ', category: 'Major US Cities' },
    { value: 'philadelphia, pa', label: 'Philadelphia, PA', category: 'Major US Cities' },
    { value: 'san antonio, tx', label: 'San Antonio, TX', category: 'Major US Cities' },
    { value: 'san diego, ca', label: 'San Diego, CA', category: 'Major US Cities' },
    { value: 'dallas, tx', label: 'Dallas, TX', category: 'Major US Cities' },
    { value: 'san jose, ca', label: 'San Jose, CA', category: 'Major US Cities' },
    // International
    { value: 'london, uk', label: 'London, UK', category: 'International' },
    { value: 'toronto, canada', label: 'Toronto, Canada', category: 'International' },
    { value: 'sydney, australia', label: 'Sydney, Australia', category: 'International' },
    { value: 'dubai, uae', label: 'Dubai, UAE', category: 'International' },
    { value: 'singapore', label: 'Singapore', category: 'International' },
    { value: 'tokyo, japan', label: 'Tokyo, Japan', category: 'International' }
  ];

  const steps = [
    'Business Information',
    'Industry & Keywords',
    'Platform Selection',
    'Location Targeting',
    'Contact Preferences',
    'Review & Activate'
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0:
        if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
        if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
          newErrors.phone = 'Please enter a valid phone number';
        }
        if (!formData.service.trim()) newErrors.service = 'Service description is required';
        break;
        
      case 1:
        if (!formData.industry) newErrors.industry = 'Please select your industry';
        if (formData.keywords.length === 0) newErrors.keywords = 'Please add at least one keyword';
        break;
        
      case 2:
        if (formData.platforms.length === 0) newErrors.platforms = 'Please select at least one platform';
        break;
        
      case 3:
        if (formData.geoTargeting.enabled && formData.geoTargeting.locations.length === 0) {
          newErrors.locations = 'Please add at least one location when location targeting is enabled';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors before continuing',
        severity: 'error'
      });
      return;
    }

    setStepLoading(true);
    // Simulate step processing
    await new Promise(resolve => setTimeout(resolve, 500));
    setStepLoading(false);
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addKeyword = () => {
    if (customKeyword.trim() && !formData.keywords.includes(customKeyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, customKeyword.trim()]
      }));
      setCustomKeyword('');
      setErrors(prev => ({ ...prev, keywords: null }));
    }
  };

  const removeKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
    setErrors(prev => ({ ...prev, platforms: null }));
  };

  const toggleLocation = (location) => {
    setFormData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        locations: prev.geoTargeting.locations.includes(location)
          ? prev.geoTargeting.locations.filter(l => l !== location)
          : [...prev.geoTargeting.locations, location]
      }
    }));
    setErrors(prev => ({ ...prev, locations: null }));
  };

  const toggleGeoTargeting = () => {
    setFormData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        enabled: !prev.geoTargeting.enabled
      }
    }));
  };

  const addCustomLocation = () => {
    if (customLocation.trim() && !formData.geoTargeting.locations.includes(customLocation.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        geoTargeting: {
          ...prev.geoTargeting,
          locations: [...prev.geoTargeting.locations, customLocation.trim().toLowerCase()]
        }
      }));
      setCustomLocation('');
      setErrors(prev => ({ ...prev, locations: null }));
    }
  };

  const removeLocation = (location) => {
    setFormData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        locations: prev.geoTargeting.locations.filter(l => l !== location)
      }
    }));
  };

  const saveProgress = async () => {
    setIsSaving(true);
    try {
      // In a real implementation, this would save to the backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSnackbar({
        open: true,
        message: 'Progress saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to save progress',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors before activating',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('https://social-media-backend.andrewrkloc.workers.dev/api/leads/activate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          geoTargeting: formData.geoTargeting
        }),
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Automatic lead generation activated:', data);
        setIsSuccess(true);
        // Clear saved progress after successful activation
        localStorage.removeItem('onboardingProgress');
      } else {
        console.error('❌ Failed to activate:', data.error);
        setSnackbar({
          open: true,
          message: `Failed to activate lead generation: ${data.error}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error activating lead generation:', error);
      setSnackbar({
        open: true,
        message: 'Error connecting to server. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                required
                error={!!errors.businessName}
                helperText={errors.businessName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                required
                error={!!errors.contactName}
                helperText={errors.contactName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="What service do you provide?"
                value={formData.service}
                onChange={(e) => handleInputChange('service', e.target.value)}
                placeholder="e.g., Real estate agent, Plumbing contractor, Marketing consultant"
                required
                error={!!errors.service}
                helperText={errors.service}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.industry}>
                <InputLabel>Your Industry</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  label="Your Industry"
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.industry && <Typography variant="body2" color="error">{errors.industry}</Typography>}
              </FormControl>
            </Grid>
            
            {formData.industry && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Suggested Keywords for {industries.find(i => i.value === formData.industry)?.label}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {industries.find(i => i.value === formData.industry)?.keywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      onClick={() => {
                        if (!formData.keywords.includes(keyword)) {
                          handleInputChange('keywords', [...formData.keywords, keyword]);
                        }
                      }}
                      color={formData.keywords.includes(keyword) ? 'primary' : 'default'}
                      variant={formData.keywords.includes(keyword) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Custom Keywords
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Add custom keyword"
                  value={customKeyword}
                  onChange={(e) => setCustomKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                  placeholder="e.g., luxury homes, emergency repair"
                  error={!!errors.keywords}
                  helperText={errors.keywords}
                />
                <Button variant="outlined" onClick={addKeyword} disabled={stepLoading}>
                  Add
                </Button>
              </Box>
              
              {formData.keywords.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.keywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      onDelete={() => removeKeyword(keyword)}
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Social Media Platforms
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We'll monitor these platforms for potential leads who engage with content related to your industry.
              </Typography>
            </Grid>
            
            {platforms.map((platform) => (
              <Grid item xs={12} md={6} key={platform.value}>
                <Card 
                  variant={formData.platforms.includes(platform.value) ? 'elevation' : 'outlined'}
                  sx={{ 
                    cursor: 'pointer',
                    borderColor: formData.platforms.includes(platform.value) ? 'primary.main' : 'divider',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => togglePlatform(platform.value)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircle 
                        color={formData.platforms.includes(platform.value) ? 'primary' : 'disabled'} 
                      />
                      <Box>
                        <Typography variant="h6">{platform.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {platform.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Location Targeting
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Target leads from specific locations to focus on your service area.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.geoTargeting.enabled}
                    onChange={toggleGeoTargeting}
                    disabled={stepLoading}
                  />
                }
                label="Enable location-based lead filtering"
              />
              {errors.locations && <Typography variant="body2" color="error">{errors.locations}</Typography>}
            </Grid>

            {formData.geoTargeting.enabled && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Select Target Locations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Choose the areas where you want to find leads:
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Popular Locations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Quick select from popular locations:
                  </Typography>
                  
                  {['South Carolina', 'Major US Cities', 'International'].map((category) => (
                    <Box key={category} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {category}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {popularLocations
                          .filter(location => location.category === category)
                          .map((location) => (
                            <Chip
                              key={location.value}
                              label={location.label}
                              onClick={() => toggleLocation(location.value)}
                              color={formData.geoTargeting.locations.includes(location.value) ? 'primary' : 'default'}
                              variant={formData.geoTargeting.locations.includes(location.value) ? 'filled' : 'outlined'}
                              sx={{ mb: 1 }}
                            />
                          ))}
                      </Box>
                    </Box>
                  ))}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Add Custom Location
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enter any city, state, or country (e.g., "Miami, FL", "Toronto, Canada", "London, UK"):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Custom location"
                      value={customLocation}
                      onChange={(e) => setCustomLocation(e.target.value)}
                      placeholder="e.g., Miami, FL or London, UK"
                      onKeyPress={(e) => e.key === 'Enter' && addCustomLocation()}
                      error={!!errors.locations}
                      helperText={errors.locations}
                    />
                    <Button
                      variant="contained"
                      onClick={addCustomLocation}
                      disabled={!customLocation.trim() || stepLoading}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                {formData.geoTargeting.locations.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Selected Locations ({formData.geoTargeting.locations.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.geoTargeting.locations.map((location) => (
                        <Chip
                          key={location}
                          label={location}
                          onDelete={() => removeLocation(location)}
                          color="primary"
                          variant="filled"
                        />
                      ))}
                    </Box>
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Unlimited Locations:</strong> You can add as many locations as you need. We'll only send you leads from these areas.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                How would you like to receive leads?
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoContact}
                    onChange={(e) => handleInputChange('autoContact', e.target.checked)}
                    disabled={stepLoading}
                  />
                }
                label="Automatically contact leads on my behalf"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                We'll send personalized messages to qualified leads using your business information.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>You'll receive:</strong>
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Email color="primary" /></ListItemIcon>
                    <ListItemText primary="Email notifications for new leads" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Phone color="primary" /></ListItemIcon>
                    <ListItemText primary="SMS alerts for hot leads (optional)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><AutoAwesome color="primary" /></ListItemIcon>
                    <ListItemText primary="AI-scored leads with contact information" />
                  </ListItem>
                </List>
              </Alert>
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Your Setup
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Business color="primary" sx={{ mr: 1 }} />
                    Business Information
                  </Typography>
                  <Typography variant="body2"><strong>Business:</strong> {formData.businessName}</Typography>
                  <Typography variant="body2"><strong>Contact:</strong> {formData.contactName}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {formData.phone}</Typography>
                  <Typography variant="body2"><strong>Service:</strong> {formData.service}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                    Lead Generation Settings
                  </Typography>
                  <Typography variant="body2"><strong>Industry:</strong> {industries.find(i => i.value === formData.industry)?.label}</Typography>
                  <Typography variant="body2"><strong>Platforms:</strong> {formData.platforms.join(', ')}</Typography>
                  <Typography variant="body2"><strong>Keywords:</strong> {formData.keywords.length} selected</Typography>
                  <Typography variant="body2"><strong>Location Targeting:</strong> {formData.geoTargeting.enabled ? `${formData.geoTargeting.locations.length} location(s)` : 'Disabled'}</Typography>
                  <Typography variant="body2"><strong>Auto-contact:</strong> {formData.autoContact ? 'Yes' : 'No'}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>What happens next:</strong>
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Schedule color="success" /></ListItemIcon>
                    <ListItemText primary="Your automatic lead generation system will be activated immediately" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Psychology color="success" /></ListItemIcon>
                    <ListItemText primary="AI will monitor social media 24/7 for qualified leads in your industry" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Notifications color="success" /></ListItemIcon>
                    <ListItemText primary="You'll receive notifications when high-quality leads are found" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Send color="success" /></ListItemIcon>
                    <ListItemText primary="Leads will be automatically contacted (if enabled) or delivered to you" />
                  </ListItem>
                </List>
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (isSuccess) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Card sx={{ maxWidth: 800, mx: 'auto' }}>
          <CardContent>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              🎉 You're All Set!
            </Typography>
            <Typography variant="h6" gutterBottom>
              Automatic Lead Generation Activated
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your system is now running in the background, scanning social media for qualified leads in your industry.
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>What to expect:</strong>
                  </Typography>
                  <Typography variant="body2">
                    • First leads typically arrive within 24-48 hours
                  </Typography>
                  <Typography variant="body2">
                    • You'll receive email notifications for each qualified lead
                  </Typography>
                  <Typography variant="body2">
                    • Leads are pre-scored and ready for immediate contact
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Your setup summary:</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Industry: {industries.find(i => i.value === formData.industry)?.label}
                  </Typography>
                  <Typography variant="body2">
                    • Platforms: {formData.platforms.join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    • Keywords: {formData.keywords.length} selected
                  </Typography>
                  <Typography variant="body2">
                    • Auto-contact: {formData.autoContact ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={() => window.location.href = '/'}
                startIcon={<TrendingUp />}
              >
                Go to Dashboard
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                onClick={() => {
                  setActiveStep(0);
                  setIsSuccess(false);
                  setFormData({
                    businessName: '',
                    contactName: '',
                    email: '',
                    phone: '',
                    industry: '',
                    service: '',
                    autoContact: false,
                    platforms: ['linkedin', 'facebook'],
                    keywords: [],
                    geoTargeting: {
                      enabled: false,
                      locations: [],
                      radius: 50
                    }
                  });
                }}
                startIcon={<AutoAwesome />}
              >
                Set Up Another Business
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ textAlign: isMobile ? 'center' : 'left' }}>
          🚀 Set Up Automatic Lead Generation
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<School />}
          onClick={() => window.location.href = '/tutorial'}
          size={isMobile ? "small" : "medium"}
        >
          View Tutorial
        </Button>
      </Box>
      
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, maxWidth: 800, mx: 'auto', px: 2 }}>
        Get qualified leads delivered to your inbox automatically. Our AI monitors social media 24/7 
        and finds people interested in your services. No manual work required!
      </Typography>

      <Card sx={{ maxWidth: 1200, mx: 'auto' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stepper 
            activeStep={activeStep} 
            sx={{ mb: 4 }}
            orientation={isMobile ? "vertical" : "horizontal"}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{isMobile ? label.split(' ')[0] : label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {stepLoading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Button
              disabled={activeStep === 0 || stepLoading}
              onClick={handleBack}
              startIcon={<ArrowBack />}
              fullWidth={isMobile}
            >
              Back
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto'
              }}>
                <Button
                  variant="outlined"
                  onClick={saveProgress}
                  disabled={isSaving || stepLoading}
                  startIcon={<Save />}
                  fullWidth={isMobile}
                >
                  {isSaving ? 'Saving...' : 'Save Progress'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting || stepLoading}
                  startIcon={<AutoAwesome />}
                  fullWidth={isMobile}
                >
                  {isSubmitting ? 'Activating...' : 'Activate Lead Generation'}
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && (!formData.businessName || !formData.contactName || !formData.email || !formData.phone || !formData.service)) ||
                  (activeStep === 1 && !formData.industry) ||
                  (activeStep === 2 && formData.platforms.length === 0) ||
                  stepLoading
                }
                startIcon={<ArrowForward />}
                fullWidth={isMobile}
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseSnackbar}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientOnboarding;
